import { createSelector } from 'reselect';
import {
  FormEntityInterface,
  MgState,
  OriginEntityInterface,
  SubmissionEntityInterface,
} from '@types';
import _isEmpty from 'lodash/isEmpty';
import _isEqual from 'lodash/isEqual';
import _uniqBy from 'lodash/uniqBy';
import { cleanObject, findUsers, findUsersIds, parseJson } from '@utils';
import { currentUserSelector, hasFeaturePermissionsSelectorFactory } from '@selectors/user';
import { FeaturePermission, PagePermission, PermissionActions, SubmissionTypes } from '@enums';

export const submissionsSelector = (store: MgState): SubmissionEntityInterface[] =>
  store?.submission?.submissions;

export const submissionsIdsSelector = createSelector(
  [submissionsSelector],
  (submissions) => submissions?.map((item) => item.id) || []
);

export const currentSubmissionSelector = (store: MgState): SubmissionEntityInterface =>
  store?.submission?.currentSubmission;

export const currentSubmissionOriginSelector = (store: MgState): OriginEntityInterface =>
  store?.submission?.currentSubmission?.origin;

export const currentSubmissionTemplateEditorFormChanged = (store: MgState): boolean =>
  store?.submission?.isCurrentSubmissionTemplateEditorFormChanged;

export const currentSubmissionGroupId = createSelector(
  [currentSubmissionSelector],
  (currentSubmission) => currentSubmission?.groups.reduce((acc, group) => group.id, '')
);

export const currentSubmissionOwnerSelector = createSelector(
  [currentSubmissionSelector],
  (currentSubmission) => currentSubmission?.owner
);

export const rendererDataSelector = createSelector(
  [currentSubmissionOriginSelector],
  (origin) => origin?.formRenderer?.data
);

export const currentSubmissionOriginSections = createSelector(
  [currentSubmissionOriginSelector],
  (currentSubmissionOrigin) => currentSubmissionOrigin?.originSections
);

export const submissionFiltersSelector = (state: MgState) => state.lettersPage?.filters;

export const isOwnFilterSelector = createSelector(
  [submissionFiltersSelector],
  (filters) => filters?.submissionType === SubmissionTypes.Own
);

export const canRemoveSubmissionsSelector = createSelector(
  [
    hasFeaturePermissionsSelectorFactory(
      PagePermission.Letters,
      [FeaturePermission.SubmissionItem],
      [PermissionActions.Remove]
    ),
  ],
  (canRemove) => canRemove
);

export const canUpdateSubmissionsSelector = createSelector(
  [
    hasFeaturePermissionsSelectorFactory(
      PagePermission.Letters,
      [FeaturePermission.SubmissionItem],
      [PermissionActions.Update]
    ),
  ],
  (canUpdate) => canUpdate
);

export const canRemoveSampleSubmissionsSelector = createSelector(
  [
    hasFeaturePermissionsSelectorFactory(
      PagePermission.Letters,
      [FeaturePermission.SubmissionSample],
      [PermissionActions.Remove]
    ),
  ],
  (canRemove) => canRemove
);

export const canSaveAsTransferToCommonSubmissions = createSelector(
  [
    hasFeaturePermissionsSelectorFactory(
      PagePermission.Letters,
      [FeaturePermission.SubmissionCommon],
      [PermissionActions.Transfer]
    ),
  ],
  (canTransfer) => canTransfer
);

export const canSaveAsTransferToOwnSubmissions = createSelector(
  [
    hasFeaturePermissionsSelectorFactory(
      PagePermission.Letters,
      [FeaturePermission.SubmissionItem],
      [PermissionActions.Transfer]
    ),
  ],
  (canTransfer) => canTransfer
);

export const canTransferCommonSubmissionsSelector = createSelector(
  [
    hasFeaturePermissionsSelectorFactory(
      PagePermission.Letters,
      [FeaturePermission.SubmissionCommon],
      [PermissionActions.Transfer]
    ),
    isOwnFilterSelector,
  ],
  (canTransfer, isOwnFilter) => canTransfer && !isOwnFilter
);

export const canUpdateCommonSubmissionsDataSelector = createSelector(
  [
    hasFeaturePermissionsSelectorFactory(
      PagePermission.Letters,
      [FeaturePermission.SubmissionCommon],
      [PermissionActions.Update]
    ),
    currentUserSelector,
    currentSubmissionOwnerSelector,
  ],
  (canUpdateSampleSubmission, currentUser, currentSubmissionOwner) => {
    if (currentUser?.id === currentSubmissionOwner?.id) {
      return true;
    }
    return canUpdateSampleSubmission;
  }
);

export const canUpdateSampleSubmissionsDataSelector = createSelector(
  [
    hasFeaturePermissionsSelectorFactory(
      PagePermission.Letters,
      [FeaturePermission.SubmissionSample],
      [PermissionActions.Update]
    ),
  ],
  (canUpdateSampleSubmission) => canUpdateSampleSubmission
);

export const canDownloadSampleSubmissionsSelector = createSelector(
  [
    hasFeaturePermissionsSelectorFactory(
      PagePermission.Letters,
      [FeaturePermission.SubmissionSample],
      [PermissionActions.Download]
    ),
  ],
  (canUpdateSampleSubmission) => canUpdateSampleSubmission
);

export const canDownloadCommonSubmissionsSelector = createSelector(
  [
    hasFeaturePermissionsSelectorFactory(
      PagePermission.Letters,
      [FeaturePermission.SubmissionCommon],
      [PermissionActions.Download]
    ),
  ],
  (canUpdateSampleSubmission) => canUpdateSampleSubmission
);

export const canRemoveCommonSubmissionsSelector = createSelector(
  [
    hasFeaturePermissionsSelectorFactory(
      PagePermission.Letters,
      [FeaturePermission.SubmissionCommon],
      [PermissionActions.Remove]
    ),
  ],
  (canUpdateSampleSubmission) => canUpdateSampleSubmission
);

export const canTransferOwnSubmissionsSelector = createSelector(
  [
    hasFeaturePermissionsSelectorFactory(
      PagePermission.Letters,
      [FeaturePermission.SubmissionItem],
      [PermissionActions.Transfer]
    ),
    isOwnFilterSelector,
  ],
  (canTransfer, isOwnFilter) => canTransfer && isOwnFilter
);

export const canUpdateSubmissionSelector = createSelector(
  [
    hasFeaturePermissionsSelectorFactory(
      PagePermission.Letters,
      [FeaturePermission.SubmissionItem],
      [PermissionActions.Update]
    ),
  ],
  (canUpdate) => canUpdate
);

export const canCreateSubmissionSelector = createSelector(
  [
    hasFeaturePermissionsSelectorFactory(
      PagePermission.Letters,
      [FeaturePermission.SubmissionItem],
      [PermissionActions.Create]
    ),
  ],
  (canCreate) => canCreate
);

export const canDownloadSubmissionSelector = createSelector(
  [
    hasFeaturePermissionsSelectorFactory(
      PagePermission.Letters,
      [FeaturePermission.SubmissionItem],
      [PermissionActions.Download]
    ),
  ],
  (canDownload) => canDownload
);

export const canViewOriginEditorSelector = createSelector(
  [
    hasFeaturePermissionsSelectorFactory(
      PagePermission.Letters,
      [FeaturePermission.TemplateEditor],
      [PermissionActions.View]
    ),
  ],
  (canView) => canView
);

export const canUpdateTemplateEditorSelector = createSelector(
  [
    hasFeaturePermissionsSelectorFactory(
      PagePermission.Letters,
      [FeaturePermission.TemplateEditor],
      [PermissionActions.Update]
    ),
  ],
  (canUpdate) => canUpdate
);

export const currentSubmissionFormSelector = (store: MgState): FormEntityInterface =>
  store?.submission?.currentSubmission?.origin?.form;

export const submissionLengthSelector = createSelector(
  [submissionsSelector],
  (submissions): number => submissions?.length
);

export const currentSubmissionIsSampleSelector = createSelector(
  [currentSubmissionSelector],
  (currentSubmission) => currentSubmission?.isSample
);

export const currentSubmissionIsCommonSelector = createSelector(
  [currentSubmissionSelector],
  (currentSubmission) => currentSubmission?.isCommon
);

export const currentSubmissionTitleSelector = createSelector(
  [currentSubmissionSelector],
  (currentSubmission) => currentSubmission?.title
);

export const currentSubmissionDescriptionSelector = createSelector(
  [currentSubmissionSelector],
  (currentSubmission) => currentSubmission?.description
);

export const currentSubmissionInitialTitleSelector = (state: MgState) =>
  state.submission.currentSubmissionInitialTitle;

export const currentSubmissionInitialDescriptionSelector = (state: MgState) =>
  state.submission.currentSubmissionInitialDescription;

export const currentSubmissionInitialDataSelector = (state: MgState) =>
  parseJson(state.submission.currentSubmissionInitialData);

export const submissionsIsEmptySelector = createSelector([submissionsSelector], (submissions) =>
  _isEmpty(submissions)
);

export const currentSubmissionDataSelector = createSelector(
  [currentSubmissionSelector],
  (submission) => parseJson(submission?.data)
);

export const currentSubmissionIdSelector = createSelector(
  [currentSubmissionSelector],
  (currentSubmission) => currentSubmission?.id
);

export const currentSubmissionFormDataSelector = createSelector(
  [currentSubmissionFormSelector],
  (form) => parseJson(form?.data)
);

export const submissionFormDownloadExtensionsSelector = createSelector(
  [currentSubmissionFormDataSelector],
  (formData) => formData?.extensions
);

export const submissionUsersIdsSelector = createSelector(
  [currentSubmissionDataSelector, currentUserSelector],
  (data, currentUser) =>
    Array.from(
      new Set([
        ...findUsersIds(data, ['searchNameRu', 'searchNameEng', 'fullNameRu', 'fullNameEng']),
        currentUser.id,
      ])
    )
);
export const submissionUsersDataSelector = createSelector(
  [currentSubmissionDataSelector, currentUserSelector],
  (data, currentUser) => {
    return _uniqBy([...findUsers(data, ['fullNameRu', 'fullNameEng']), currentUser], 'id');
  }
);

export const currentSubmissionLoadingSelector = (state: MgState) =>
  state?.submission.isCurrentSubmissionLoading;

export const isCurrentSubmissionTemplateEditorModeOn = (state: MgState) =>
  state?.submission.isCurrentSubmissionTemplateEditorModeOn;

export const currentSubmissionUpdateLoadingSelector = (state: MgState) =>
  state?.submission.isSubmissionUpdateLoading;

export const currentSubmissionActionDisabledSelector = createSelector(
  [currentSubmissionLoadingSelector, currentSubmissionUpdateLoadingSelector],
  (isCurrentSubmissionLoading, isCurrentSubmissionUpdateLoading) => {
    if (isCurrentSubmissionLoading || isCurrentSubmissionUpdateLoading) {
      return true;
    }
  }
);

export const isCurrentSubmissionPrivateSubmissionSelector = createSelector(
  [
    currentSubmissionOwnerSelector,
    currentUserSelector,
    currentSubmissionIsCommonSelector,
    currentSubmissionIsSampleSelector,
  ],
  (currentSubmissionOwner, currentUser, isCommon, isSample) =>
    !isCommon && !isSample && currentSubmissionOwner?.id !== currentUser?.id
);

export const canViewPrivateSubmissionsSelector = createSelector(
  [
    hasFeaturePermissionsSelectorFactory(
      PagePermission.Letters,
      [FeaturePermission.SubmissionPrivate],
      [PermissionActions.View]
    ),
  ],
  (canViewPrivateTemplates) => canViewPrivateTemplates
);

export const canRemovePrivateSubmissionsSelector = createSelector(
  [
    hasFeaturePermissionsSelectorFactory(
      PagePermission.Letters,
      [FeaturePermission.SubmissionPrivate],
      [PermissionActions.Remove]
    ),
  ],
  (canRemovePrivateSubmissions) => canRemovePrivateSubmissions
);

export const canDownloadPrivateSubmissionsSelector = createSelector(
  [
    hasFeaturePermissionsSelectorFactory(
      PagePermission.Letters,
      [FeaturePermission.SubmissionPrivate],
      [PermissionActions.Download]
    ),
  ],
  (canDownloadPrivateSubmissions) => canDownloadPrivateSubmissions
);

export const canUpdatePrivateSubmissionsSelector = createSelector(
  [
    hasFeaturePermissionsSelectorFactory(
      PagePermission.Letters,
      [FeaturePermission.SubmissionPrivate],
      [PermissionActions.Update]
    ),
  ],
  (canUpdatePrivateSubmissions) => canUpdatePrivateSubmissions
);

export const canCreateSampleSubmissionSelector = createSelector(
  [
    hasFeaturePermissionsSelectorFactory(
      PagePermission.Letters,
      [FeaturePermission.SubmissionSample],
      [PermissionActions.Create]
    ),
  ],
  (canCreateSampleSubmission) => canCreateSampleSubmission
);

export const canTransferSampleSubmissionSelector = createSelector(
  [
    hasFeaturePermissionsSelectorFactory(
      PagePermission.Letters,
      [FeaturePermission.SubmissionSample],
      [PermissionActions.Transfer]
    ),
  ],
  (canTransferSampleSubmission) => canTransferSampleSubmission
);

export const canTransferPrivateSubmissionsSelector = createSelector(
  [
    hasFeaturePermissionsSelectorFactory(
      PagePermission.Letters,
      [FeaturePermission.SubmissionPrivate],
      [PermissionActions.Transfer]
    ),
  ],
  (canTransferPrivateTemplates) => canTransferPrivateTemplates
);

export const titleChangeableSelector = createSelector(
  [
    currentSubmissionIsCommonSelector,
    canUpdateCommonSubmissionsDataSelector,
    currentSubmissionIsSampleSelector,
    canUpdateSampleSubmissionsDataSelector,
    isCurrentSubmissionPrivateSubmissionSelector,
    canUpdatePrivateSubmissionsSelector,
  ],
  (
    isCommon,
    canUpdateCommonSubmission,
    isSample,
    canUpdateSampleSubmission,
    isPrivate,
    canUpdatePrivateSubmission
  ) => {
    if (isSample) {
      return canUpdateSampleSubmission;
    }
    if (isCommon) {
      return canUpdateCommonSubmission;
    }
    if (isPrivate) {
      return canUpdatePrivateSubmission;
    }
    return true;
  }
);

export const titleInputDisabledSelector = createSelector(
  [currentSubmissionActionDisabledSelector, titleChangeableSelector, canUpdateSubmissionSelector],
  (currentSubmissionActionDisabled, titleChangeable, canUpdate) =>
    !titleChangeable || currentSubmissionActionDisabled || !canUpdate
);

export const currentDescriptionDisplayedSelector = createSelector(
  [titleInputDisabledSelector, currentSubmissionDescriptionSelector],
  (titleInputDisabled, currentSubmissionDescription) =>
    !(titleInputDisabled && currentSubmissionDescription === '')
);

export const submissionHasChangesSelector = createSelector(
  [
    currentSubmissionTitleSelector,
    currentSubmissionDataSelector,
    currentSubmissionInitialTitleSelector,
    currentSubmissionInitialDataSelector,
    currentSubmissionIsSampleSelector,
    currentSubmissionSelector,
    currentSubmissionDescriptionSelector,
    currentSubmissionInitialDescriptionSelector,
    currentSubmissionIsCommonSelector,
    canUpdateSampleSubmissionsDataSelector,
    canUpdateCommonSubmissionsDataSelector,
  ],
  (
    currentSubmissionTitle,
    currentSubmissionData,
    currentSubmissionInitialTitle,
    currentSubmissionInitialData,
    currentSubmissionIsSample,
    currentSubmission,
    currentSubmissionDescription,
    currentSubmissionInitialDescription,
    currentSubmissionIsCommon,
    canUpdateSampleSubmission,
    canUpdateCommonSubmission
  ) => {
    if (!currentSubmission) {
      return false;
    }

    if (currentSubmissionIsSample && !canUpdateSampleSubmission) {
      return false;
    }

    if (currentSubmissionIsCommon && !canUpdateCommonSubmission) {
      return false;
    }

    if (currentSubmissionData) {
      const keys = Object.keys(currentSubmissionData);
      for (const key of keys) {
        const field = currentSubmissionData[key];
        if (typeof field === 'object' && !Object.keys(cleanObject(field)).length) {
          delete currentSubmissionData[key];
        }
      }
    }
    return (
      currentSubmissionInitialTitle !== currentSubmissionTitle ||
      (!_isEqual(cleanObject(currentSubmissionInitialData), currentSubmissionData) &&
        !currentSubmissionIsSample) ||
      currentSubmissionInitialDescription !== currentSubmissionDescription
    );
  }
);

export const submissionSavableSelector = createSelector(
  [
    submissionHasChangesSelector,
    currentSubmissionActionDisabledSelector,
    currentSubmissionIsCommonSelector,
    canUpdateCommonSubmissionsDataSelector,
    currentSubmissionIsSampleSelector,
    canUpdateSampleSubmissionsDataSelector,
    isCurrentSubmissionPrivateSubmissionSelector,
    canUpdatePrivateSubmissionsSelector,
    isCurrentSubmissionTemplateEditorModeOn,
    currentSubmissionTemplateEditorFormChanged,
    canUpdateTemplateEditorSelector,
  ],
  (
    hasChanges,
    isCurrentSubmissionActionDisabled,
    isCommon,
    canUpdateCommonSubmission,
    isSample,
    canUpdateSampleSubmission,
    isPrivate,
    canUpdatePrivateSubmission,
    isTemplateEditorModeOn,
    isCurrentSubmissionTemplateEditorFormChanged,
    canUpdateTemplateEditor
  ) => {
    if (isTemplateEditorModeOn) {
      if (!canUpdateTemplateEditor) {
        return false
      }
      return isCurrentSubmissionTemplateEditorFormChanged
    }
    if (hasChanges && !isCurrentSubmissionActionDisabled) {
      if (isCommon) {
        return canUpdateCommonSubmission;
      }
      if (isSample) {
        return canUpdateSampleSubmission;
      }
      if (isPrivate) {
        return canUpdatePrivateSubmission;
      }
      return true;
    } else {
      return false;
    }
  }
);

export const submissionDeletableSelector = createSelector(
  [
    currentSubmissionSelector,
    currentSubmissionIsSampleSelector,
    currentSubmissionOwnerSelector,
    currentSubmissionActionDisabledSelector,
    currentUserSelector,
    submissionsSelector,
    canRemoveSampleSubmissionsSelector,
    currentSubmissionIsCommonSelector,
    canRemoveCommonSubmissionsSelector,
    canRemoveSubmissionsSelector,
    canRemovePrivateSubmissionsSelector,
    isCurrentSubmissionPrivateSubmissionSelector,
  ],
  (
    currentSubmission,
    currentSubmissionIsSample,
    currentSubmissionOwner,
    isCurrentSubmissionActionDisabled,
    currentUser,
    submissions,
    canRemoveSample,
    isCommon,
    canRemoveCommonSubmission,
    canRemoveSubmission,
    canRemovePrivateSubmissions,
    isPrivate
  ) => {
    if (!currentSubmission) {
      return false;
    }

    if (!submissions.length) {
      return false;
    }

    if (isCurrentSubmissionActionDisabled) {
      return false;
    }

    if (currentSubmissionIsSample) {
      return canRemoveSample;
    }

    if (canRemoveSubmission && currentSubmissionOwner?.id === currentUser?.id) {
      return true;
    }

    if (isCommon) {
      return canRemoveCommonSubmission;
    }

    if (isPrivate) {
      return canRemovePrivateSubmissions;
    }
  }
);

export const showCheckboxListItemSelector = createSelector(
  [
    canTransferCommonSubmissionsSelector,
    canTransferOwnSubmissionsSelector,
    canRemoveSubmissionsSelector,
    canUpdateSubmissionsSelector,
    isOwnFilterSelector,
    isCurrentSubmissionPrivateSubmissionSelector,
    canTransferPrivateSubmissionsSelector,
  ],
  (
    canTransferCommon,
    canTransferOwn,
    canRemoveSubmissions,
    canUpdateSubmissions,
    isOwnFilter,
    isPrivate,
    canTransferPrivate
  ) => {
    if (isPrivate) {
      return canTransferPrivate;
    }
    return (
      canTransferCommon ||
      canTransferOwn ||
      (isPrivate && canTransferPrivate) ||
      (isOwnFilter && (canRemoveSubmissions || canUpdateSubmissions))
    );
  }
);

export const submissionDownloadableSelector = createSelector(
  [
    currentSubmissionSelector,
    currentSubmissionActionDisabledSelector,
    currentSubmissionIsSampleSelector,
    canDownloadSampleSubmissionsSelector,
    currentSubmissionIsCommonSelector,
    canDownloadCommonSubmissionsSelector,
    isCurrentSubmissionPrivateSubmissionSelector,
    canDownloadPrivateSubmissionsSelector,
  ],
  (
    currentSubmission,
    isCurrentSubmissionActionDisabled,
    isSampleSubmission,
    canDownloadSample,
    isCommonSubmission,
    canDownloadCommonSubmission,
    isPrivateSubmission,
    canDownloadPrivateSubmissions
  ) => {
    if (!currentSubmission) {
      return false;
    }
    if (!isCurrentSubmissionActionDisabled) {
      if (isSampleSubmission) {
        return canDownloadSample;
      }
      if (isCommonSubmission) {
        return canDownloadCommonSubmission;
      }
      if (isPrivateSubmission) {
        return canDownloadPrivateSubmissions;
      }
      return true;
    } else return false;
  }
);

export const isSubmissionSaveAsDisabledSelector = createSelector(
  [
    currentSubmissionSelector,
    currentSubmissionLoadingSelector,
    currentSubmissionUpdateLoadingSelector,
    isCurrentSubmissionTemplateEditorModeOn,
    canCreateSampleSubmissionSelector,
  ],
  (
    currentSubmission,
    currentSubmissionLoading,
    currentSubmissionUpdateLoading,
    isTemplateEditorModeOn,
    canCreateSample
  ) => {
    if (isTemplateEditorModeOn) {
      return !canCreateSample;
    }
    return !currentSubmission || currentSubmissionLoading || currentSubmissionUpdateLoading;
  }
);

export const isOriginEditableSelector = createSelector(
  [canViewOriginEditorSelector, currentSubmissionSelector, currentSubmissionIsSampleSelector],
  (canUpdateOrigin, currentSubmission, isSample) => {
    if (currentSubmission && canUpdateOrigin) {
      return canUpdateOrigin && isSample && currentSubmission.origin.originSections.length > 0;
    }

    return false;
  }
);
